// src/pages/PosPage.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Box, Grid, TextField, Typography, CircularProgress, Alert, Button, Paper } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';

// Importaciones de Contextos y Servicios
import AuthContext from '../context/AuthContext.js';
import { useLayout } from '../context/LayoutContext.js';
import api from '../api/index.js';
import { useOnlineStatus } from '../hooks/useOnlineStatus.js';
import { addSaleToOfflineQueue, syncPendingSales } from '../services/OfflineService.js';
import { printTicket } from '../services/printTicket.js';

// Importaciones de Componentes Hijos
import ProductCard from '../components/pos/ProductCard.js';
import Cart from '../components/pos/Cart.js';
import PaymentModal from '../components/pos/PaymentModal.js';
import OpenCashDrawer from '../components/pos/OpenCashDrawer.js';
import CategoryFilter from '../components/pos/CategoryFilter.js';
import CreditPaymentModal from '../components/pos/CreditPaymentModal.js';

const PosPage = () => {
    // --- ESTADOS ---
    const { user, isCashSessionOpen, cashSession, checkCashSessionStatus, loading: authLoading } = useContext(AuthContext);
    const { setHeaderTitle } = useLayout();
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [currentStore, setCurrentStore] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [openCreditModal, setOpenCreditModal] = useState(false);
    const [saleSuccess, setSaleSuccess] = useState(null);
    const isOnline = useOnlineStatus();
    const storeId = useMemo(() => user?.stores?.[0], [user]);

    // --- EFECTOS ---
    useEffect(() => {
        if (isOnline) { syncPendingSales(); }
        window.addEventListener('online', syncPendingSales);
        return () => window.removeEventListener('online', syncPendingSales);
    }, [isOnline]);

    useEffect(() => {
        if (currentStore?.name) {
            setHeaderTitle(`TPV - ${currentStore.name}`);
        }
        return () => setHeaderTitle('');
    }, [currentStore, setHeaderTitle]);

    const loadPrerequisites = useCallback(async () => {
        if (isCashSessionOpen && storeId) {
            setLoading(true);
            setErrorMessage('');
            try {
                const storeRes = await api.get(`/stores/${storeId}`);
                const currentStoreData = storeRes.data;
                setCurrentStore(currentStoreData);
                const inventoryStoreId = currentStoreData.inventory_source_store_id || storeId;
                const [inventoryRes, customersRes, categoriesRes] = await Promise.all([
                    api.get(`/inventory/store/${inventoryStoreId}?timestamp=${new Date().getTime()}`),
                    api.get(`/customers?timestamp=${new Date().getTime()}`),
                    api.get('/categories')
                ]);
                const processedInventory = inventoryRes.data.map(item => ({...item, purchase_price: parseFloat(item.purchase_price) || 0, sale_price: parseFloat(item.sale_price) || 0}));
                setProducts(processedInventory);
                setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
                setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
            } catch (err) {
                setErrorMessage('No se pudieron cargar los datos del TPV.');
                console.error("Error al cargar prerrequisitos:", err);
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, [isCashSessionOpen, storeId]);

    useEffect(() => {
        if (user) { loadPrerequisites(); }
    }, [isCashSessionOpen, user, loadPrerequisites]);

    useEffect(() => {
        let tempProducts = products;
        if (selectedCategory) tempProducts = tempProducts.filter(p => p.category_name === selectedCategory);
        if (searchTerm) tempProducts = tempProducts.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.sku.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredProducts(tempProducts);
    }, [searchTerm, selectedCategory, products]);

    // --- MANEJADORES DE EVENTOS ---
    const addToCart = (product) => {
        setCart(currentCart => {
            const existingItem = currentCart.find(item => item.id === product.product_id);
            if (existingItem) {
                return currentCart.map(item =>
                    item.id === product.product_id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...currentCart, {
                id: product.product_id, name: product.name, quantity: 1,
                sale_price: product.sale_price, purchase_price: product.purchase_price,
            }];
        });
    };

    const updateLocalInventory = () => {
        setProducts(currentProducts => {
            const productsMap = new Map(currentProducts.map(p => [p.product_id, { ...p }]));
            cart.forEach(cartItem => {
                if (productsMap.has(cartItem.id)) {
                    const product = productsMap.get(cartItem.id);
                    product.quantity -= cartItem.quantity;
                    productsMap.set(cartItem.id, product);
                }
            });
            return Array.from(productsMap.values());
        });
    };

    const handleFinalizeSale = async (payments, customerId = null) => {
        if (!cashSession?.id) {
            alert("Error: No hay una sesión de caja activa.");
            return;
        }
        const salePayload = {
            userId: user.id, storeId: storeId, cart: cart,
            payments: payments, cashSessionId: cashSession.id, customerId: customerId
        };
        try {
            const response = await api.post('/sales', salePayload);
            setSaleSuccess({ ...response.data, cartForPrint: cart });
            setOpenPaymentModal(false);
            setCart([]);
        } catch (err) {
            if (err.code === 'ERR_NETWORK' || !navigator.onLine) {
                alert('No hay conexión. La venta se guardó para sincronizarse más tarde.');
                try {
                    await addSaleToOfflineQueue(salePayload);
                    updateLocalInventory();
                    setCart([]);
                    setOpenPaymentModal(false);
                } catch (offlineErr) {
                    alert("No se pudo guardar la venta localmente.");
                }
            } else {
                alert(err.response?.data?.message || 'No se pudo completar la venta.');
            }
        }
    };
    
    const handleNewSale = () => {
        setSaleSuccess(null);
        loadPrerequisites();
    };

    const handleAbonoSuccess = () => {
        loadPrerequisites();
    };

    // --- RENDERIZADO ---
    if (authLoading || loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
    }
    if (errorMessage) {
        return <Alert severity="error" sx={{ m: 2 }}>{errorMessage}</Alert>;
    }
    if (!isCashSessionOpen) {
        return <OpenCashDrawer onCashDrawerOpen={checkCashSessionStatus} />;
    }

    if (saleSuccess) {
        const receiptUrl = `${window.location.origin}/receipt/${saleSuccess.receipt_uuid}`;
        const ticketData = {
            saleId: saleSuccess.saleId,
            saleDate: new Date(),
            storeName: currentStore?.name,
            items: saleSuccess.cartForPrint,
            totalAmount: saleSuccess.cartForPrint.reduce((sum, item) => sum + item.quantity * Number(item.sale_price || 0), 0)
        };
        const handlePrintClick = () => {
            printTicket(ticketData);
        };
        return (
            <Box sx={{ textAlign: 'center', mt: 4, p: 3 }}>
                <Typography variant="h4" color="success.main" gutterBottom>¡Venta Realizada con Éxito!</Typography>
                <Typography>Puntos Ganados: {saleSuccess.pointsAwarded} {saleSuccess.pointsName}</Typography>
                <Box sx={{ my: 4 }}><QRCodeSVG value={receiptUrl} size={256} /></Box>
                <Typography>Escanea el código QR para ver tu recibo digital.</Typography>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button variant="outlined" size="large" onClick={handlePrintClick}>
                        Imprimir Ticket
                    </Button>
                    <Button variant="contained" size="large" onClick={handleNewSale}>
                        Nueva Venta
                    </Button>
                </Box>
            </Box>
        );
    }
    
    return (
        <>
            <Box sx={{ display: 'flex', height: 'calc(100vh - 96px)', gap: 2 }}>
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <TextField label="Buscar producto por nombre o SKU" variant="outlined" fullWidth sx={{ mb: 2 }} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    <CategoryFilter categories={categories} selectedCategory={setSelectedCategory} onSelectCategory={setSelectedCategory} />
                    <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                        <Grid container spacing={2}>
                            {filteredProducts.map(product => (
                                <Grid item key={product.product_id} xs={12} sm={6} lg={4} xl={3}>
                                    <ProductCard product={product} onAddToCart={addToCart} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
                <Paper sx={{ width: {md: '40%', lg: '35%'}, minWidth: '350px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Cart
                        cart={cart}
                        setCart={setCart}
                        onProceedToPayment={() => setOpenPaymentModal(true)}
                        onOpenCreditModal={() => setOpenCreditModal(true)}
                        store={currentStore}
                    />
                </Paper>
            </Box>
            <PaymentModal
                open={openPaymentModal}
                onClose={() => setOpenPaymentModal(false)}
                total={cart.reduce((sum, item) => sum + item.quantity * Number(item.sale_price || 0), 0)}
                customers={customers}
                onFinalizeSale={handleFinalizeSale}
            />
            <CreditPaymentModal
                open={openCreditModal}
                onClose={() => setOpenCreditModal(false)}
                customers={customers}
                cashSessionId={cashSession?.id}
                onPaymentSuccess={handleAbonoSuccess}
            />
        </>
    );
};

export default PosPage;